import { LightThemeTable } from "components/LightThemeTable";
import ProjectCSVDownload from "components/ProjectCSVDownload";
import Config from "config";
import Constants from "helpers/constants";
import { getItem } from "helpers/storage";
import React, { useEffect, useRef, useState } from "react";
import { connect, ConnectedProps, useDispatch } from "react-redux";
import { State } from "state";
import { EventTypes, Status } from "state/analytics";
import { CHAIN_ROLE } from "state/auth";
import { fetchChainRole } from "state/chain_user_role_fetch";
import { unsetError } from "state/create_file_upload";
import { fetchLoggedUserChains } from "state/logged_user_chains";
import { fetchUsersByProjectId } from "state/users";
import { buildExportFilename, getTemplateHeaders } from "utils/helpers";
import FileUploadForStep4CP from "./FileUploadForStep4CP";
// import { getItem } from "helpers/storage";
// import { chainRole } from "state/auth";

interface User {
  chain_role?: string | number;
  id: number;
  userRole: string;
  firstName: string;
  lastName: string;
  email: string;
  stores: any[];
  displayName?: string;
  company?: string;
  role?: string;
  hasaccess?: boolean;
}

interface OwnProps {
  projectId?: number | string;
  updateProject?: (id: string, data: any) => Promise<any>;
  createAnalyticsEvent?: (event: any) => void;
  project: any;
  hasUserToStoreMappingError?: boolean;
  isprocessing?: boolean;
  onUsersChange?: (users: any[]) => void; // ✅ new callback
}

type Props = OwnProps & PropsFromRedux;

const Step4Form: React.FC<Props> = ({
  projectId,
  updateProject,
  createAnalyticsEvent,
  project,
  hasUserToStoreMappingError = false,
  fetchedUsers,
  fetchUsersByProjectId,
  upload,
  unsetError,
  loadingUsers,
  chainUserRole,
  user,
  isprocessing = false,
                                      onUsersChange
}) => {
  const dispatch = useDispatch();
  const [users, setUsers] = useState<User[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isExternalAdmin, setExternalAdmin] = useState(false);
  const prevLoadingRef = React.useRef<boolean>(false);
  let label = "PROJECT USER MAPPING";
  const [isProcessing, setIsProcessing] = useState(false);
  const [userStoreMappings, setUserStoreMappings] = useState< { project_id: number | string; store_id: any; user_id: number }[] >([]);
  const [isChainChecking, setIsChainChecking] = useState(false);
  // Fetch logged user chains on component mount
  useEffect(() => {
    dispatch(fetchLoggedUserChains());
  }, [dispatch]);

    useEffect(() => {
      if (project?.chain?.id) {
        dispatch(fetchChainRole(project.chain.id));
      }
    }, [ dispatch]);

  useEffect(() => {
    if (projectId) {
      fetchUsersByProjectId(projectId);
    }
  }, [projectId, fetchUsersByProjectId]);

  useEffect(() => {
    if (prevLoadingRef.current && !upload.loading && !upload.hasError && projectId) {
      fetchUsersByProjectId(projectId);
    }

    prevLoadingRef.current = upload.loading || false;
  }, [upload.loading, upload.hasError, projectId, fetchUsersByProjectId]);

  // Load users from localStorage if present, else from fetchedUsers
  useEffect(() => {
  const localKey = projectId ? `step4_users_${projectId}` : "step4_users";
  const saved = localStorage.getItem(localKey);

  if (fetchedUsers && fetchedUsers.length > 0) {
    const processingFlag = fetchedUsers[0]?.is_data_processing ?? false;
    setIsProcessing(processingFlag);

    const mappedUsers = fetchedUsers?.map((user: any, index: number) => ({
      id: user.id || index + 1,
      userRole: user.role || "User role 1",
      firstName: user.displayName?.split(" ")[0] || "First Name",
      lastName: user.displayName?.split(" ").slice(1).join(" ") || "Last Name",
      email: user.email || "",
      stores: user.stores || "All",
      displayName: user.displayName,
      company: user.company,
      role: user.role,
      chain_role: user.chain_role,
      hasaccess: user.hasaccess ?? false,
      is_data_processing: user.is_data_processing ?? false,
    }));

    // Merge saved access map into fetched users
    let mergedUsers = mappedUsers;
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Handle new access map format { [userId]: hasaccess }
        if (parsed && !Array.isArray(parsed)) {
          mergedUsers = mappedUsers?.map((u: any) => ({
            ...u,
            hasaccess: parsed[u.id] ?? u.hasaccess,
          }));
        }
        // Handle old full array format (backward compatibility)
        if (Array.isArray(parsed)) {
          mergedUsers = mappedUsers.map((u: any) => {
            const savedUser = parsed.find((s: any) => s.id === u.id);
            return savedUser
              ? {
                  ...u,
                  hasaccess: savedUser.hasaccess,
                  stores: savedUser.stores || u.stores,
                }
              : u;
          });
        }
      } catch {}
    }

    setUsers(mergedUsers);
    setCurrentPage(1);

    if (processingFlag) {
      const newMappings = mergedUsers
        .filter((eachUser: any) => eachUser.hasaccess === true)
        ?.map((eachUser: any) => ({
          project_id: projectId!,
          store_id: Array.isArray(eachUser.stores)
            ? eachUser.stores[0]?.store_id ?? 0 
            : 0,
          user_id: eachUser.id,
          hasaccess: true,
        }));

      setUserStoreMappings(newMappings);
      if (typeof onUsersChange === "function") {
        onUsersChange(newMappings);
      }
    }
  }
}, [fetchedUsers, projectId]);

  useEffect(() => {
    return () => {
      unsetError("USER2STORE");
      unsetError("USERS");
    };
  }, [unsetError]);


  const handleAfterUpload = (type: "User to Store Mapping") => {
    if (project && createAnalyticsEvent) {
      createAnalyticsEvent({
        event: EventTypes.CREATE_NEW_PROJECT_UPLOAD,
        eventType: "Action",
        ...(project.name &&
          project.chain &&
          project.chain.name && {
            projectName: `${project.chain.name} - ${project.name}`,
          }),
        projectType: project.project_type,
        fileUploadType: type,
        fileUploadStatus: hasUserToStoreMappingError ? Status.FAILURE : Status.SUCCESS,
      });
    }
  };

  const handlePageChange = (page: number, newLimit: number) => {
    setCurrentPage(page);
    setLimit(newLimit);
  };

  useEffect(() => {
    if (user?.role !== Constants.AB_SYSTEM_ADMIN) {
      const hasExternalAdmin = chainUserRole?.some((item: any) => {
        if (Array.isArray(item.roles)) {
          return item.roles.some((r: any) => r.role === "External Project Admin");
        }
        return item.role === "External Project Admin";
      });
    if(hasExternalAdmin){setExternalAdmin(true)}
  }

    if (user?.role === "External Project Admin") {
      setExternalAdmin(true);
    }
  }, [chainUserRole, user?.role]);

  const isUserInternalAdmin = (userId: number): boolean => {
    const userRow = users.find((u) => u.id === userId);
    return userRow?.chain_role === "Internal Project Admin";
  };
  // const isUserExternalAdmin = (userId: number): boolean => {
  //   const userRow = users.find((u) => u.id === userId);
  //   return userRow?.chain_role === "External Project Admin";
  // };

  const handleReportAccessChange = (userId: number, checked: boolean, storeId: number) => {
    // If this user is an Internal Project Admin, they cannot uncheck the Report Access
    if (isUserInternalAdmin(userId) && !checked) { return; }

    setUsers((prevUsers) => {
      const updated = prevUsers?.map((u) =>
        u.id === userId ? { ...u, hasaccess: checked } : u
      );

      // Store only the minimal access state to avoid localStorage quota errors
      const localKey = projectId ? `step4_users_${projectId}` : "step4_users";
      const accessMap = updated.reduce((acc: Record<number, boolean>, u: any) => {
        acc[u.id] = !!u.hasaccess;
        return acc;
      }, {});

      try {
        localStorage.setItem(localKey, JSON.stringify(accessMap));
      } catch (e) {
        console.warn("localStorage limit reached for Step 4 users:", e);
        try {
          localStorage.removeItem(localKey);
        } catch (removeError) {
          console.warn("Failed to clear localStorage after quota error:", removeError);
        }
      }

      // Send checked and unchecked users
      const newMappings = updated?.map((eachUser: any) => ({
        project_id: projectId!,
        store_id: Array.isArray(eachUser.stores)
          ? eachUser.stores[0]?.store_id ?? 0
          : 0,
        user_id: eachUser.id,
        hasaccess: !!eachUser.hasaccess,
      }));

      if (isProcessing && typeof onUsersChange === "function") {
        onUsersChange(newMappings);
      }

      return updated;
    });
  };
  const chainPermissionString = getItem(CHAIN_ROLE);
  const chainPermission = JSON.parse(chainPermissionString || "[]");
  const permission = chainPermission;

  const isReportEditableAccess =
    user?.role === Constants.AB_SYSTEM_ADMIN || permission.role_name === Constants.INTERNAL_PROJECT_ADMIN;

  const isExternalProjectAdminRole = permission.role_name === "External Project Admin";
  const showTableLoader = loadingUsers || isChainChecking;
  const originalAccessRef = useRef<Map<string, boolean>>(new Map());
  useEffect(() => {
    if (!isProcessing || !users?.length) return;

    const processed = new Set<string>();

    users.forEach((row: User) => {
      const isInternalAdmin = isUserInternalAdmin(row.id);

      if (isInternalAdmin && row.hasaccess !== true && !processed.has(row.id.toString())) {
        processed.add(row.id.toString());

        // Update UI state
        row.hasaccess = true;

        // end default access to backend
        handleReportAccessChange(
          row.id,
          true,
          row.stores[0]?.store_id
        );
      }
    });
  }, [isProcessing, users]);
  
  useEffect(() => {
    users.forEach((row: User) => {
      const userId = String(row.id);

      if (!originalAccessRef.current.has(userId)) {
        originalAccessRef.current.set(userId, !!row.hasaccess);
      }
    });
  }, [users]);

  const columns = [
    {
      field: "userRole",
      header: "User role",
      formatter: (_: any, row: User) => <div style={cellStyle}>{row.chain_role}</div>,
    },
    {
      field: "firstName",
      header: "First Name",
      formatter: (_: any, row: User) => <div style={cellStyle}>{row.firstName}</div>,
    },
    {
      field: "lastName",
      header: "Last Name",
      formatter: (_: any, row: User) => <div style={cellStyle}>{row.lastName}</div>,
    },
    {
      field: "email",
      header: "Email",
      formatter: (_: any, row: User) => <div style={cellStyle}>{row.email}</div>,
    },
    {
      field: "stores",
      header: "Stores",
      formatter: (_: any, row: User, index: any) => (
        <div key={index} style={cellStyle}>
          <select
            value={row.stores}
            style={styles.storeSelect}
          >
            {row.stores?.map((item: any, index: number) => {
              return (
                <option key={index} value="1 store">
                  {item?.retail_store_num}
                </option>
              );
            })}
          </select>
        </div>
      ),
    },

    //   TO DO: ENABLE THIS COLUMN WHEN BACKEND SUPPORT IS ADDED
    ...(isProcessing && !isExternalProjectAdminRole
      ? [
          {
            field: "reportaccess",
            header: "Report Access",
            formatter: (_: any, row: User) => {
              const userIsInternalAdmin = isUserInternalAdmin(row.id);
              // Internal Project Admin must always have access
              if (userIsInternalAdmin && row.hasaccess !== true) {
                row.hasaccess = true;
              }

              // ONLY System Admin can edit
              const disabled = !isReportEditableAccess;
              const disabledColor = (isReportEditableAccess && (userIsInternalAdmin) || (!isReportEditableAccess))

              const tooltip = disabled
                ? "Only System Admin can modify Report Access"
                : "";

              return (
                <div
                  style={{
                    ...cellStyle,
                    left: "25px",
                    position: "relative",
                    opacity: disabledColor ? 0.5 : 1,
                    pointerEvents: disabledColor ? "none" : "auto",
                    cursor: disabledColor ? "not-allowed" : "pointer",
                  }}
                  title={tooltip}
                >
                  <input
                    type="checkbox"
                    checked={!!row.hasaccess}
                    disabled={disabled}
                    onChange={(e) =>
                      handleReportAccessChange(
                        row.id,
                        e.target.checked,
                        row.stores[0]?.store_id
                      )
                    }
                    className="custom-checkbox"
                    aria-label="Report Access"
                  />
                </div>
              );
            },
          },
        ]
      : [])
  ];

  return (
    <div style={styles.container}>
      <>
        <div style={styles.header}>
        <h3 style={styles.title}>Users - Store mapping</h3>
        <ProjectCSVDownload
          data={[]}
          headers={getTemplateHeaders(label)}
          filename={buildExportFilename("csv", `${label}-template`)}
        >
          <div className="file-upload-header">Download CSV Template</div>
        </ProjectCSVDownload>
        </div>

        <FileUploadForStep4CP
          prefix={"USER2STORE"}
          url={`${Config.API_SERVICE_URL}/api/users-stores`}
          label={""}
          user={user}
          dataQa={"user-store-mapping-file-upload"}
          afterUploadFunction={() => {
            if (projectId && updateProject) {
              updateProject(`${projectId}`, {});
            }

            handleAfterUpload("User to Store Mapping");
            // Clear localStorage for this project on upload
            const localKey = projectId ? `step4_users_${projectId}` : "step4_users";
            // localStorage.removeItem(localKey);
          }}
          project={project}
          isExternalAdmin={isExternalAdmin}
          onChainCheckingChange={setIsChainChecking}
        />
      </>

      <h4 style={styles.sectionTitle}>Select the Users & Store mapping</h4>

      <LightThemeTable
        data={users}
        columns={columns}
        onPageChange={handlePageChange}
        pageSizeOptions={[5, 10, 20, 50, 100]}
        isLoading={showTableLoader}
      />
    </div>
  );
};

const connecter = connect(
  (state: State, ownProps: OwnProps) => {
    return {
      project: state.project.content,
      fetchedUsers: state.users.content?.data,
      loadingUsers: state.users.loading,
      upload: {
        // @ts-ignore
        ...state["USER2STORE_file_upload"],
      },
      chainUserRole: state.chainUserRole.chainUserRole,
      user: state.auth_user.content,
    };
  },
  { fetchUsersByProjectId, unsetError }
);

type PropsFromRedux = ConnectedProps<typeof connecter>;

export default connecter(Step4Form);

const cellStyle: React.CSSProperties = {
  paddingTop: "12px",
  paddingBottom: "12px",
  fontSize: "14px",
  color: "#374151",
};

const styles = {
  container: {
    padding: "0",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
  },
  title: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#000",
    margin: 0,
  },
  downloadLink: {
    color: "#f59e0b",
    fontSize: "14px",
    textDecoration: "none",
    fontWeight: 500,
    cursor: "pointer",
  },
  uploadBox: {
    border: "1.5px dashed #d1d5db",
    borderRadius: "12px",
    padding: "5px 20px",
    textAlign: "center" as const,
    cursor: "pointer",
    transition: "all 0.3s ease",
    backgroundColor: " rgba(227, 175, 50, 0.05)",
    marginBottom: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "100%",
  },
  uploadBoxHover: {
    borderColor: "#f59e0b",
    backgroundColor: "#fffbf0",
  },
  uploadIcon: {
    fontSize: "32px",
    marginRight: "12px",
    opacity: 0.5,
  },
  uploadText: {
    fontSize: "15px",
    color: "#6b7280",
    margin: 0,
  },
  uploadLink: {
    color: "#f59e0b",
    fontWeight: 500,
  },
  sectionTitle: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#000",
    marginBottom: "8px",
    marginTop: 15,
  },
  storeSelect: {
    padding: "6px 30px 6px 12px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    fontSize: "13px",
    color: "#6b7280",
    cursor: "pointer",
    backgroundColor: "white",
    appearance: "none" as const,
    backgroundImage:
      "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 9L1 4h10z'/%3E%3C/svg%3E\")",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 10px center",
    minWidth: "120px",
  },
};


